import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import ResumeUploadModal from './ResumeUploadModal';
import JDUploadModal from './JDUploadModal';
import './LoginInterface.css';

const LoginInterface = ({ onCallEnd }) => {
  const { t } = useTranslation();
  const {
    loading,
    error,
    login,
    showResumeModal,
    handleResumeUploadSuccess,
    closeResumeModal,
    showJDModal,
    handleJDUploadSuccess,
    closeJDModal
  } = useAuth();
  const [isRegistering, setIsRegistering] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: ''
  });
  const [registerError, setRegisterError] = useState(null);
  const [registerSuccess, setRegisterSuccess] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (onCallEnd) onCallEnd(); // Reset call end state
    setRegisterError(null);
    setRegisterSuccess(null);

    try {
      await login(formData.email, formData.password);
    } catch (error) {
      // Error is already handled in AuthContext
      // Just log it here for debugging
      console.log('Login attempt completed with error handling');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError(null);
    setRegisterSuccess(null);

    try {
      // Validate registration fields
      if (!formData.firstName || !formData.firstName.trim()) {
        setRegisterError('First name is required');
        return;
      }
      if (!formData.lastName || !formData.lastName.trim()) {
        setRegisterError('Last name is required');
        return;
      }
      if (!formData.email || !formData.email.trim()) {
        setRegisterError('Email is required');
        return;
      }
      if (!formData.password || formData.password.length < 6) {
        setRegisterError('Password must be at least 6 characters');
        return;
      }

      const response = await apiService.register(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName
      );

      console.log('✅ Registration successful:', response);
      setRegisterSuccess('Registration successful! Please login with your credentials.');

      // Clear form and switch to login mode after 2 seconds
      setTimeout(() => {
        setFormData({
          email: formData.email,
          password: '',
          firstName: '',
          lastName: ''
        });
        setIsRegistering(false);
        setRegisterSuccess(null);
      }, 2000);

    } catch (error) {
      console.error('❌ Registration failed:', error);
      setRegisterError(error.message || 'Registration failed. Please try again.');
    }
  };

  const toggleMode = () => {
    setIsRegistering(!isRegistering);
    setRegisterError(null);
    setRegisterSuccess(null);
    // Clear password but keep email
    setFormData({
      ...formData,
      password: '',
      firstName: '',
      lastName: ''
    });
  };

  return (
    <div className="login-interface">
      <div className="login-container">
        <div className="login-header">
          <h1>{isRegistering ? 'Create Account' : t('login.portalTitle')}</h1>
          <p>{isRegistering ? 'Register to get started' : t('login.subtitle')}</p>
        </div>

        <form onSubmit={isRegistering ? handleRegister : handleLogin} className="login-form">
          {isRegistering && (
            <>
              <div className="form-group">
                <label htmlFor="firstName">First Name:</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name:</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  required
                  disabled={loading}
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="email">{t('login.emailLabel') || 'Email'}:</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('login.emailPlaceholder') || 'Enter your email'}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('login.passwordLabel') || 'Password'}:</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={isRegistering ? 'Create a password (min 6 characters)' : t('login.passwordPlaceholder') || 'Enter your password'}
              required
              disabled={loading}
              minLength={isRegistering ? 6 : undefined}
            />
          </div>

          <button type="submit" className="start-interview-btn" disabled={loading}>
            {loading ? t('common.connecting') : isRegistering ? 'Register' : t('login.startInterview') || 'Login'}
          </button>
        </form>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {registerError && (
          <div className="error-message">
            {registerError}
          </div>
        )}

        {registerSuccess && (
          <div className="success-message" style={{
            padding: '10px',
            marginTop: '10px',
            backgroundColor: '#4CAF50',
            color: 'white',
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            {registerSuccess}
          </div>
        )}

        <div className="auth-toggle" style={{
          marginTop: '15px',
          textAlign: 'center',
          fontSize: '14px'
        }}>
          {isRegistering ? (
            <span>
              Already have an account?{' '}
              <button
                type="button"
                onClick={toggleMode}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007bff',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                Login here
              </button>
            </span>
          ) : (
            <span>
              Don't have an account?{' '}
              <button
                type="button"
                onClick={toggleMode}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007bff',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                Register here
              </button>
            </span>
          )}
        </div>
      </div>

      {/* Resume Upload Modal */}
      <ResumeUploadModal
        isOpen={showResumeModal}
        onClose={closeResumeModal}
        onUploadSuccess={handleResumeUploadSuccess}
      />

      {/* Job Description Upload Modal */}
      <JDUploadModal
        isOpen={showJDModal}
        onClose={closeJDModal}
        onUploadSuccess={handleJDUploadSuccess}
      />
    </div>
  );
};

export default LoginInterface;